import { useState } from 'react';
import type { ReactNode } from 'react';
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { FiX } from 'react-icons/fi';
import { styles } from '@/shared/app-stepper-form/styles';
import { StepSheetBody } from '@/shared/app-stepper-form/parts/step-sheet-body';
import { StepSheetTrigger } from '@/shared/app-stepper-form/parts/step-sheet-trigger';
import type { StepState } from '@/shared/app-stepper-form/hooks/use-stepper';
import type { StepDefinition } from '@/shared/app-stepper-form/types';

/**
 * The step list, as a bottom sheet on a phone.
 *
 * WHY A SHEET AND NOT A HEADER
 * ----------------------------
 * The mobile header used to carry the step name, the count and a progress bar
 * permanently. That is a third of a phone's usable height spent on a caption the
 * user cannot act on: they can see they are on step 2, but not what steps 3 and
 * 4 are, and not how to get back to step 1.
 *
 * The sheet keeps the same information one tap away and gives the form the space
 * back. It also restores the thing the desktop rail has and the phone did not:
 * the ability to SEE the whole journey and jump to any step already reached.
 *
 * WHY IT REUSES `StepRail`
 * ------------------------
 * The sheet and the desktop aside must agree on what a step looks like and which
 * steps are reachable. Rendering the same component in both places means the
 * status words, the markers and the jump rules cannot drift apart. The only
 * difference is the layout classes, which `StepRail` now accepts.
 *
 * WHY IT CARRIES THE ACTIVE STEP'S DESCRIPTION
 * --------------------------------------------
 * On a phone the panel heading shows only the step's name, because a permanent
 * description under it wrapped to three or four lines and pushed the first field
 * of every step below the fold. The description is orientation rather than
 * instruction — worth reading once, not on every step — so it moved here, where
 * it sits directly above the step it describes and costs the form no height.
 *
 * It is rendered for the ACTIVE step only. Listing every step's description would
 * turn a short index into a wall of prose, and the user only needs to know what
 * the step they are on is for.
 *
 * WHY IT DOES NOT USE THE SHARED `DialogContent`
 * ----------------------------------------------
 * `DialogContent` hard-codes a centred viewport and a full-screen backdrop, which
 * is right for a confirmation and wrong for a sheet: a sheet is anchored to the
 * bottom edge and slides up. Rather than add a variant to the shared dialog and
 * risk the confirmations that already depend on it, the sheet composes the Base
 * UI primitive directly. It still gets the focus trap, the escape handling and
 * the `role="dialog"` that the primitive supplies.
 */
export function StepSheet({
  step,
  steps,
  states,
  activeIndex,
  furthestReached,
  onJump,
}: {
  step: StepDefinition;
  steps: ReadonlyArray<StepDefinition>;
  states: ReadonlyArray<StepState>;
  activeIndex: number;
  furthestReached: number;
  onJump: (index: number) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);

  /**
   * Jumping closes the sheet.
   *
   * The user picked a step to work on, so leaving the sheet open would hide the
   * form they just asked for. Closing first also means focus returns to the
   * trigger rather than to a row inside a sheet that is about to unmount.
   */
  const jump = (index: number): void => {
    onJump(index);
    setOpen(false);
  };

  return (
    <>
      <StepSheetTrigger
        index={activeIndex}
        total={steps.length}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
      />

      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Backdrop className={styles.sheetBackdrop} />
          <DialogPrimitive.Viewport className={styles.sheetViewport}>
            <DialogPrimitive.Popup className={styles.sheetPopup} aria-modal="true">
              <div className={styles.sheetHeader}>
                <DialogPrimitive.Title className={styles.sheetTitle}>
                  Complete steps
                </DialogPrimitive.Title>
                <DialogPrimitive.Close className={styles.sheetClose} aria-label="Close steps">
                  <FiX aria-hidden="true" className="size-4" />
                </DialogPrimitive.Close>
              </div>
              <StepSheetBody
                step={step}
                steps={steps}
                states={states}
                activeIndex={activeIndex}
                furthestReached={furthestReached}
                onJump={jump}
              />
            </DialogPrimitive.Popup>
          </DialogPrimitive.Viewport>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
